import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Shield, ArrowRight, AlertTriangle, Eye, ChevronRight, Loader2, RotateCcw, Lock, Gem, BrainCircuit, Layers, TrendingUp } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: "illusion", title: "The Illusion", subtitle: "What your bank balance says" },
  { id: "truth", title: "The Truth", subtitle: "Who actually owns that money" },
  { id: "risk", title: "The Risk", subtitle: "What happens without Sentient Vault" },
  { id: "engine", title: "The Engine", subtitle: "How AI protects financial truth" },
  { id: "explore", title: "Explore", subtitle: "See the full system live" },
];

export default function DemoPage() {
  const navigate = useNavigate();
  const { setToken } = useAuth();
  const [step, setStep] = useState(0);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animating, setAnimating] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => { startDemo(); }, []);

  const startDemo = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/demo/start`);
      const { token, user, trial } = res.data;
      localStorage.setItem("sv_token", token);
      localStorage.setItem("sv_user", JSON.stringify(user));
      localStorage.setItem("sv_trial", JSON.stringify(trial));
      if (setToken) setToken(token);
      const dash = await axios.get(`${API}/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } });
      setData(dash.data);
    } catch (e) { console.error("Demo start failed:", e); }
    setLoading(false);
  };

  const resetDemo = async () => {
    setResetting(true);
    try {
      const token = localStorage.getItem("sv_token");
      await axios.post(`${API}/demo/reset`, {}, { headers: { Authorization: `Bearer ${token}` } });
      const dash = await axios.get(`${API}/dashboard/summary`, { headers: { Authorization: `Bearer ${token}` } });
      setData(dash.data);
      setStep(0);
    } catch (e) { console.error(e); }
    setResetting(false);
  };

  const nextStep = () => {
    setAnimating(true);
    setTimeout(() => { setStep(s => Math.min(s + 1, STEPS.length - 1)); setAnimating(false); }, 250);
  };

  const goToDashboard = () => { window.location.href = "/dashboard"; };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#020617] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin mx-auto mb-4" />
          <p className="text-sm text-slate-400">Preparing demo experience...</p>
        </div>
      </div>
    );
  }

  const o = data?.ownership || {};
  const total = (o.owned || 0) + (o.custody || 0) + (o.restricted || 0);
  const vaults = data?.vaults || [];
  const ownedPct = total > 0 ? Math.round((o.owned || 0) / total * 100) : 0;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-50 flex flex-col" data-testid="demo-page">
      {/* Demo Header */}
      <div className="h-14 border-b border-slate-800/30 flex items-center justify-between px-6 shrink-0">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png" alt="" className="w-8 h-8" />
          <span className="font-barlow font-bold text-lg">SENTIENT VAULT</span>
          <Badge className="bg-amber-500/15 text-amber-400 border-amber-500/30 border text-[9px] font-mono" data-testid="demo-badge">DEMO MODE</Badge>
        </div>
        <div className="flex items-center gap-4">
          <div className="hidden md:flex gap-1.5">
            {STEPS.map((s, i) => (
              <button key={s.id} onClick={() => { setAnimating(true); setTimeout(() => { setStep(i); setAnimating(false); }, 250); }}
                className={`h-2 rounded-full transition-all cursor-pointer ${i === step ? 'bg-amber-400 w-8' : i < step ? 'bg-amber-400/40 w-2' : 'bg-slate-700 w-2'}`}
                data-testid={`demo-step-dot-${i}`} />
            ))}
          </div>
          <Button size="sm" variant="ghost" className="text-xs text-slate-500 gap-1.5" onClick={resetDemo} disabled={resetting} data-testid="demo-reset-btn">
            {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Reset Demo
          </Button>
          <Button size="sm" variant="outline" className="border-slate-700 text-xs gap-1" onClick={() => navigate("/register")} data-testid="demo-signup-btn">
            Create Account <ArrowRight className="w-3 h-3" />
          </Button>
        </div>
      </div>

      {/* Step Content */}
      <div className={`flex-1 flex items-center justify-center p-6 md:p-8 transition-opacity duration-250 ${animating ? 'opacity-0' : 'opacity-100'}`}>
        <div className="max-w-3xl w-full">

          {/* ── STEP 0: THE ILLUSION ── */}
          {step === 0 && (
            <div className="text-center" data-testid="demo-step-illusion">
              <span className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.3em]">Step 1 of 5</span>
              <h1 className="font-barlow font-bold text-4xl sm:text-5xl mt-4 mb-4 leading-tight">Your bank says you have</h1>
              <div className="font-mono text-5xl sm:text-7xl font-bold text-emerald-400 mb-3 tabular-nums" data-testid="demo-total-balance">
                GBP {total.toLocaleString()}
              </div>
              <p className="text-lg text-slate-400 mb-2">Meridian & Co. monthly balance.</p>
              <p className="text-sm text-slate-600 mb-8">Looks healthy. Most business owners would agree.</p>
              <Button onClick={nextStep} className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold px-8 h-12 rounded-xl text-sm" data-testid="demo-next-btn">
                But who actually owns it? <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* ── STEP 1: THE TRUTH ── */}
          {step === 1 && (
            <div data-testid="demo-step-truth">
              <span className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.3em]">Step 2 of 5</span>
              <h1 className="font-barlow font-bold text-3xl sm:text-4xl mt-4 mb-2">Here's who actually owns that money</h1>
              <p className="text-slate-500 mb-6">Only <span className="text-amber-400 font-bold">{ownedPct}%</span> of GBP {total.toLocaleString()} is truly yours to use.</p>
              <div className="space-y-2.5 mb-6">
                {[
                  { label: "Tax owed to HMRC", vault: "TAX_CUSTODY", amount: vaults.find(v => v.vault_type === "TAX_CUSTODY")?.balance || 0, color: "#EF4444", tag: "NOT YOURS", icon: Shield },
                  { label: "Owed to suppliers", vault: "SUPPLIER_OBLIGATION", amount: vaults.find(v => v.vault_type === "SUPPLIER_OBLIGATION")?.balance || 0, color: "#F59E0B", tag: "NOT YOURS", icon: Lock },
                  { label: "Customer deposits held in trust", vault: "CUSTOMER_TRUST", amount: vaults.find(v => v.vault_type === "CUSTOMER_TRUST")?.balance || 0, color: "#3B82F6", tag: "NOT YOURS", icon: Shield },
                  { label: "Inventory capital locked", vault: "INVENTORY_CUSTODY", amount: vaults.find(v => v.vault_type === "INVENTORY_CUSTODY")?.balance || 0, color: "#8B5CF6", tag: "CUSTODY", icon: Layers },
                  { label: "Loan capital restricted", vault: "OPERATING_CUSTODY", amount: vaults.find(v => v.vault_type === "OPERATING_CUSTODY")?.balance || 0, color: "#6366F1", tag: "RESTRICTED", icon: Lock },
                  { label: "YOUR actual money", vault: "FREE_EQUITY", amount: o.owned || 0, color: "#FFD700", tag: "YOURS", icon: Gem },
                ].map((item) => (
                  <div key={item.vault} className="flex items-center justify-between p-3 rounded-xl border border-slate-800/40 bg-slate-900/30"
                    style={{ borderLeftWidth: "3px", borderLeftColor: item.color }}>
                    <div className="flex items-center gap-3">
                      <item.icon className="w-4 h-4 shrink-0" style={{ color: item.color }} />
                      <span className="text-sm text-slate-300">{item.label}</span>
                      <Badge className={`text-[8px] font-mono border-none ${item.tag === "YOURS" ? "bg-amber-500/15 text-amber-400" : "bg-red-500/10 text-red-400/80"}`}>{item.tag}</Badge>
                    </div>
                    <span className="text-base font-mono tabular-nums font-bold" style={{ color: item.color }}>GBP {item.amount.toLocaleString()}</span>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl border border-amber-500/20 bg-amber-500/[0.04] text-center mb-6">
                <span className="text-sm text-slate-400">Of GBP {total.toLocaleString()}, only</span>
                <span className="font-mono text-3xl font-bold text-amber-400 block mt-1" data-testid="demo-owned-amount">GBP {(o.owned || 0).toLocaleString()}</span>
                <span className="text-sm text-slate-500">is truly yours</span>
              </div>
              <Button onClick={nextStep} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold h-12 rounded-xl" data-testid="demo-next-btn">
                What happens if you ignore this? <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* ── STEP 2: THE RISK ── */}
          {step === 2 && (
            <div data-testid="demo-step-risk">
              <span className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.3em]">Step 3 of 5</span>
              <h1 className="font-barlow font-bold text-3xl sm:text-4xl mt-4 mb-2">Without Sentient Vault, this happens</h1>
              <p className="text-slate-500 mb-6">The owner spends GBP 50,000 thinking it's profit. Here's the cascade:</p>
              <div className="space-y-2.5 mb-6">
                {[
                  { event: "Owner withdraws GBP 50k as \"profit\"", impact: "Free Equity drops to -GBP 7,500 (negative equity)", severity: "CRITICAL" },
                  { event: "VAT payment due in 14 days (GBP 52k)", impact: "Tax Custody can't cover — HMRC penalty triggered", severity: "CRITICAL" },
                  { event: "Supplier payment due in 7 days (GBP 38k)", impact: "Must delay — supply chain at risk", severity: "CRITICAL" },
                  { event: "Loan repayment due in 10 days (GBP 8.5k)", impact: "May default — triggers penalty clause", severity: "HIGH" },
                  { event: "Customer delivery due in 30 days (GBP 85k)", impact: "GBP 85k deposit at risk if delivery fails", severity: "HIGH" },
                ].map((r, i) => (
                  <div key={i} className="p-3 rounded-xl border border-red-500/15 bg-red-500/[0.03]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-slate-300">{r.event}</span>
                      <Badge className={`text-[8px] font-mono border-none ${r.severity === "CRITICAL" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>{r.severity}</Badge>
                    </div>
                    <p className="text-xs text-red-400/80">{r.impact}</p>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl border border-red-500/20 bg-red-500/[0.04] text-center mb-6">
                <AlertTriangle className="w-6 h-6 text-red-400 mx-auto mb-2" />
                <span className="text-red-400 font-barlow font-bold text-lg block">Cascade failure in 7 days</span>
                <span className="text-xs text-slate-500">This is how profitable-looking businesses collapse.</span>
              </div>
              <Button onClick={nextStep} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold h-12 rounded-xl" data-testid="demo-next-btn">
                How the engine prevents this <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* ── STEP 3: THE ENGINE ── */}
          {step === 3 && (
            <div data-testid="demo-step-engine">
              <span className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.3em]">Step 4 of 5</span>
              <h1 className="font-barlow font-bold text-3xl sm:text-4xl mt-4 mb-2">How Sentient Vault surfaces financial truth</h1>
              <p className="text-slate-500 mb-6">Every transaction is processed through a 5-layer financial truth engine:</p>
              <div className="space-y-2.5 mb-6">
                {[
                  { step: "1", label: "Upload financial records", detail: "CSV, bank exports, invoices — any format. Data enters the pool.", icon: TrendingUp, color: "text-cyan-400" },
                  { step: "2", label: "AI classifies every record", detail: "Rules engine handles obvious patterns. AI resolves ambiguity. Reconciliation verifies math.", icon: BrainCircuit, color: "text-purple-400" },
                  { step: "3", label: "Review and correct", detail: "Edit records, override classifications, recalculate. Full audit trail preserved.", icon: Eye, color: "text-amber-400" },
                  { step: "4", label: "Commit to financial truth", detail: "Approved records write to the vault ledger. Every number is traceable.", icon: Shield, color: "text-emerald-400" },
                  { step: "5", label: "Dashboard shows reality", detail: "Owned vs obligated. Pressure indicators. No more cash illusions.", icon: Gem, color: "text-amber-400" },
                ].map((r) => (
                  <div key={r.step} className="flex items-start gap-4 p-3 rounded-xl border border-slate-800/30 bg-slate-900/20">
                    <div className="w-8 h-8 rounded-lg bg-slate-800/50 flex items-center justify-center shrink-0">
                      <span className="text-xs font-mono font-bold text-slate-400">{r.step}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <r.icon className={`w-4 h-4 ${r.color}`} />
                        <span className="text-sm font-semibold text-slate-200">{r.label}</span>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">{r.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] text-center mb-6">
                <Shield className="w-6 h-6 text-emerald-400 mx-auto mb-2" />
                <span className="text-emerald-400 font-barlow font-bold text-lg block">Every vault balance is ledger-backed and auditable</span>
                <span className="text-xs text-slate-500">No shortcuts. No approximations. Real financial truth.</span>
              </div>
              <Button onClick={nextStep} className="w-full bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold h-12 rounded-xl" data-testid="demo-next-btn">
                Explore the live system <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}

          {/* ── STEP 4: EXPLORE ── */}
          {step === 4 && (
            <div className="text-center" data-testid="demo-step-explore">
              <span className="text-[10px] text-slate-600 font-mono uppercase tracking-[0.3em]">Step 5 of 5</span>
              <img src="https://customer-assets.emergentagent.com/job_vault-truth/artifacts/s3q6fukn_image.png" alt="" className="w-20 h-20 mx-auto mt-6 mb-4" />
              <h1 className="font-barlow font-bold text-3xl sm:text-4xl mt-2 mb-3">This is Sentient Vault</h1>
              <p className="text-base text-slate-400 max-w-lg mx-auto mb-4">
                The world's first obligation-enforced financial truth system. Every fund classified. Every risk visible. Every decision guided by reality.
              </p>
              <p className="text-sm text-slate-500 max-w-md mx-auto mb-8">
                The demo dashboard is loaded with Meridian & Co's data. You can explore the Data Pool, review AI classifications, adjust vaults, and generate reports.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button onClick={goToDashboard} className="bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold px-8 h-12 rounded-xl text-sm gap-2" data-testid="demo-explore-btn">
                  <Gem className="w-4 h-4" /> Explore Dashboard
                </Button>
                <Button variant="outline" className="border-slate-700 text-slate-300 h-12 px-8 rounded-xl text-sm" onClick={() => navigate("/register")} data-testid="demo-create-account-btn">
                  Create Your Account <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              <p className="text-[10px] text-slate-600 mt-8">30-day free trial. No credit card required.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
